import { hasSupplementalUiCopy } from "@/config/locale";
import type { ProductListItemFragment } from "@/gql/graphql";
import type { ProductCardData, ProductCardVariant } from "./product-card";
import { getColorHex, isColorAttribute, isSizeAttribute } from "@/lib/colors";
import { normalizeSaleorMediaUrl } from "@/lib/media-url";
import { sortSizes } from "@/lib/sizes";
import { localeConfig } from "@/config/locale";
import { publicStorePath } from "@/lib/storefront/public-url";
import { isAuthoredCopyProfile, parseProductUxMetadata } from "@/lib/product-ux";
import { hasDiscountInPriceRange } from "@/lib/pricing";
import { isDefaultCategory } from "@/lib/category/display-category";
import { getSaleUnit } from "@/lib/packaging/sale-unit";

/**
 * Map of Saleor product slugs → local Eurolab handoff peptide images.
 * Overrides Saleor's stock thumbnails with our high-quality renders for the
 * branded peptide catalog. Returns null if no override exists for the slug.
 */
const LOCAL_PEPTIDE_IMAGES: Record<string, { src: string; ext: "png" | "webp" }> = {
	// Empty — AI-generierte Overrides entfernt, Saleor liefert Thumbnails
};

function resolveLocalPeptideImage(slug: string): string | null {
	return LOCAL_PEPTIDE_IMAGES[slug]?.src ?? null;
}

/**
 * Extract colors from product variants
 */
function extractColorsFromVariants(
	variants: ProductListItemFragment["variants"],
): { name: string; hex: string }[] {
	const colorSet = new Map<string, string>();

	variants?.forEach((variant) => {
		variant.selectionAttributes?.forEach((attr) => {
			if (isColorAttribute(attr.attribute?.slug ?? "")) {
				attr.values?.forEach((value) => {
					const colorName = value.name;
					if (colorName && !colorSet.has(colorName)) {
						const hex = getColorHex(value) ?? "#6b7280"; // Default gray if no match
						colorSet.set(colorName, hex);
					}
				});
			}
		});
	});

	return Array.from(colorSet.entries()).map(([name, hex]) => ({ name, hex }));
}

/**
 * Extract sizes from product variants
 */
function extractSizesFromVariants(variants: ProductListItemFragment["variants"]): string[] {
	const sizeSet = new Set<string>();

	variants?.forEach((variant) => {
		variant.selectionAttributes?.forEach((attr) => {
			if (isSizeAttribute(attr.attribute?.slug ?? "")) {
				attr.values?.forEach((value) => {
					if (value.name) {
						sizeSet.add(value.name);
					}
				});
			}
		});
	});

	// Sort sizes in logical order (S, M, L, XL or numeric)
	return sortSizes(Array.from(sizeSet));
}

function getStrictBoxVialCount(
	variant: NonNullable<NonNullable<ProductListItemFragment["variants"]>[number]>,
): number | null {
	const rawUnits = variant.unitsPerSaleUnit?.trim() ?? "";
	if (!/^[1-9]\d*$/.test(rawUnits)) return null;
	const units = Number(rawUnits);
	if (!Number.isSafeInteger(units) || units <= 1) return null;
	const saleUnit = getSaleUnit({ saleUnit: variant.saleUnit, unitsPerSaleUnit: rawUnits });
	return saleUnit.isBox && saleUnit.unitsPerSaleUnit === units ? units : null;
}

function getSharedBoxVialCount(variants: ProductListItemFragment["variants"]): number | null {
	if (!variants?.length) return null;
	const counts = variants.map(getStrictBoxVialCount);
	if (counts.some((count) => count == null)) return null;
	const [first] = counts;
	return counts.every((count) => count === first) ? first : null;
}

/**
 * Transform Saleor product data to ProductCard format
 */
export function transformToProductCard(
	product: ProductListItemFragment,
	locale: string,
	marketChannel: string,
): ProductCardData {
	const startPrice = product.pricing?.priceRange?.start?.gross;
	const undiscountedStartPrice = product.pricing?.priceRangeUndiscounted?.start?.gross;
	const productUx = parseProductUxMetadata(product.metadata, marketChannel, locale);
	const mc = productUx.localizedMarketCopy;
	const authoredShortDescription =
		mc && isAuthoredCopyProfile(mc.copyVersion) && mc.shortDescription.trim() ? mc.shortDescription : null;

	// Use centralized pricing logic to detect if ANY variant is on sale
	const isSale = hasDiscountInPriceRange(
		product.pricing?.priceRange,
		product.pricing?.priceRangeUndiscounted,
	);

	// Extract colors and sizes from variants
	const colors = extractColorsFromVariants(product.variants);
	const sizes = extractSizesFromVariants(product.variants);
	const vialsPerSaleUnit = getSharedBoxVialCount(product.variants);
	const orderedImages = (product.media ?? []).filter((media) => media.type === "IMAGE");
	const primaryImage = orderedImages[0];
	const hoverImage = orderedImages[1];
	const variants: ProductCardVariant[] = (product.variants ?? []).map((variant) => ({
		id: variant.id,
		name: variant.translation?.name ?? variant.name,
		quantityAvailable: variant.quantityAvailable ?? null,
		price: variant.pricing?.price?.gross?.amount ?? null,
		currency: variant.pricing?.price?.gross?.currency ?? null,
	}));

	return {
		id: product.id,
		name: product.translation?.name || product.name,
		slug: product.slug,
		brand: isDefaultCategory(product.category)
			? null
			: (product.category?.translation?.name ?? product.category?.name ?? null),
		price: startPrice?.amount ?? 0,
		compareAtPrice: isSale ? undiscountedStartPrice?.amount : null,
		currency: startPrice?.currency ?? localeConfig.fallbackCurrency,
		image:
			resolveLocalPeptideImage(product.slug) ??
			(primaryImage?.url
				? normalizeSaleorMediaUrl(primaryImage.url)
				: product.thumbnail?.url
					? normalizeSaleorMediaUrl(product.thumbnail.url)
					: "/placeholder.svg"),
		imageAlt: product.translation?.name ?? product.name,
		hoverImage: hoverImage?.url ? normalizeSaleorMediaUrl(hoverImage.url) : null,
		href: publicStorePath(locale, `/products/${product.slug}`),
		badge: isSale ? "Sale" : null,
		colors,
		sizes,
		category: product.category
			? {
					id: product.category.id,
					name: product.category.translation?.name ?? product.category.name,
					slug: product.category.slug,
				}
			: null,
		createdAt: product.created,
		hasVariants: variants.length > 1,
		variants,
		shortDescription:
			authoredShortDescription ??
			(hasSupplementalUiCopy(locale)
				? product.translation?.seoDescription?.trim()
					? product.translation.seoDescription
					: null
				: (productUx.localizedMarketCopy?.shortDescription ??
					(locale === "de" ? productUx.shortDescription : null))),
		trustBadges: productUx.trustBadges,
		vialsPerSaleUnit,
	};
}

/**
 * Format price with currency
 */
export function formatPrice(amount: number, currency: string): string {
	return new Intl.NumberFormat(localeConfig.default, {
		style: "currency",
		currency: currency,
	}).format(amount);
}
